// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import { disposeGroup } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import type { HighlightableObjectName } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import {
  UnmarkedMeasurementLineGeometry,
  type UnmarkedMeasurementLineGeometryOpts,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/UnmarkedMeasurementLineGeometry';
import { ViewportScaledGroup } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/ViewportScaledGroup';

export type MarkedMeasurementLineGeometryOpts = UnmarkedMeasurementLineGeometryOpts & {
  markerType?: 'circle' | 'arrow';
};

const MARKER_HEIGHT = 0.1;
/** Fraction of the camera frustum height that each marker should occupy.*/
const MARKER_SCREEN_SIZE_FRACTION = 0.01;

export class MarkedMeasurementLineGeometry extends UnmarkedMeasurementLineGeometry {
  private readonly markers: {
    start: ViewportScaledGroup;
    end: ViewportScaledGroup;
  };
  public readonly markerMaterial: THREE.MeshBasicMaterial;
  private readonly markerType: MarkedMeasurementLineGeometryOpts['markerType'];

  constructor(
    highlightableObjectName: HighlightableObjectName,
    { markerType, ...otherOpts }: MarkedMeasurementLineGeometryOpts,
  ) {
    super(highlightableObjectName, otherOpts);
    this.markerType = markerType;
    this.markerMaterial = new THREE.MeshBasicMaterial({
      color: this.lineMaterial.color,
      depthTest: false,
    });

    const markerGeometry =
      this.markerType === 'arrow'
        ? new THREE.ConeGeometry(0.06, MARKER_HEIGHT, 4)
        : new THREE.SphereGeometry(MARKER_HEIGHT / 2, 8, 8);

    const markerMesh = new THREE.Mesh(markerGeometry, this.markerMaterial);

    if (this.markerType === 'arrow') {
      markerMesh.position.set(0, -MARKER_HEIGHT / 2, 0);
    }

    const UP = new THREE.Vector3(0, 1, 0);

    const startMarkerGroup = new ViewportScaledGroup(MARKER_SCREEN_SIZE_FRACTION, MARKER_HEIGHT);
    startMarkerGroup.add(markerMesh);
    startMarkerGroup.position.copy(this.startLocal);
    startMarkerGroup.quaternion.setFromUnitVectors(UP, this.startLocal.clone().sub(this.midpointLocal).normalize());
    this.add(startMarkerGroup);

    const endMarkerGroup = new ViewportScaledGroup(MARKER_SCREEN_SIZE_FRACTION, MARKER_HEIGHT);
    endMarkerGroup.position.copy(this.endLocal);
    endMarkerGroup.quaternion.setFromUnitVectors(UP, this.endLocal.clone().sub(this.midpointLocal).normalize());
    // Clone the geometry (not the whole mesh) so each marker group owns a distinct
    // BufferGeometry. Sharing a single geometry would cause a double-dispose when
    // disposeGroup is called on both marker groups during cleanup.
    const endMarkerMesh = new THREE.Mesh(markerGeometry.clone(), this.markerMaterial);
    if (this.markerType === 'arrow') {
      endMarkerMesh.position.set(0, -MARKER_HEIGHT / 2, 0);
    }
    endMarkerGroup.add(endMarkerMesh);
    this.add(endMarkerGroup);

    this.markers = { start: startMarkerGroup, end: endMarkerGroup };
    // Add one to the line's render order so these sit above the line.
    markerMesh.renderOrder = this.line.renderOrder + 1;
    endMarkerMesh.renderOrder = this.line.renderOrder + 1;

    const layer = otherOpts.layer;
    if (layer) {
      this.markers.start.layers.set(layer);
      this.markers.end.layers.set(layer);
      this.markers.start.children.forEach((child) => child.layers.set(layer));
      this.markers.end.children.forEach((child) => child.layers.set(layer));
    }
  }

  updateEndPoints(newStart: THREE.Vector3, newEnd: THREE.Vector3) {
    super.updateEndPoints(newStart, newEnd);
    const UP = new THREE.Vector3(0, 1, 0);

    this.markers.start.position.copy(newStart);
    this.markers.start.quaternion.setFromUnitVectors(UP, newStart.clone().sub(this.midpointLocal).normalize());
    this.markers.end.position.copy(newEnd);
    this.markers.end.quaternion.setFromUnitVectors(UP, newEnd.clone().sub(this.midpointLocal).normalize());
  }

  /**
   * Scales the markers so they maintain a constant screen size regardless of zoom level,
   * matching the behavior of `Line2` (which uses pixel-space line widths).
   *
   * Delegates to {@link ViewportScaledGroup.update} on each marker group.
   *
   * Call once per frame after the object's world matrix has been updated.
   */
  scaleForCamera(camera: THREE.Camera) {
    if (!(camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera)) return;

    this.markers.start.update(camera);
    this.markers.end.update(camera);
  }

  highlight() {
    super.highlight();
    this.markerMaterial.color.set(this.lineMaterial.color);
    this.markers.start.renderOrder = this.line.renderOrder + 1;
    this.markers.end.renderOrder = this.line.renderOrder + 1;
    this.markers.start.screenSizeFraction = MARKER_SCREEN_SIZE_FRACTION * 1.5;
    this.markers.end.screenSizeFraction = MARKER_SCREEN_SIZE_FRACTION * 1.5;
  }

  unhighlight() {
    super.unhighlight();
    this.markerMaterial.color.set(this.lineMaterial.color);
    this.markers.start.renderOrder = this.line.renderOrder + 1;
    this.markers.end.renderOrder = this.line.renderOrder + 1;
    this.markers.start.screenSizeFraction = MARKER_SCREEN_SIZE_FRACTION;
    this.markers.end.screenSizeFraction = MARKER_SCREEN_SIZE_FRACTION;
  }

  dispose() {
    // `this.markerMaterial` will be disposed by `this.markers.start` (as well as its clone on this.markers.end)
    disposeGroup(this.markers.start);
    disposeGroup(this.markers.end);
    this.remove(this.markers.start);
    this.remove(this.markers.end);
    super.dispose();
  }
}
